import { useEffect, useRef, useState } from "react";
import Logo from "../components/Logo";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";

const SUBJECTS = ["Maths","Physics","Chemistry","Biology","English","Hindi","Computer Science","History","Economics","Sanskrit"];

function TiltCard({ children, style }) {
  const ref = useRef();
  const onMove = e => {
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX-r.left)/r.width-0.5, y = (e.clientY-r.top)/r.height-0.5;
    ref.current.style.transform = `perspective(700px) rotateY(${x*8}deg) rotateX(${-y*8}deg) scale(1.02)`;
  };
  const onLeave = () => { ref.current.style.transform = "perspective(700px) rotateY(0) rotateX(0) scale(1)"; };
  return <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ transition:"transform .15s ease", ...style }}>{children}</div>;
}

const avgR = a => a?.length ? +(a.reduce((x,y)=>x+y,0)/a.length).toFixed(1) : 0;

function TeacherCard({ t }) {
  const initials = (t.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
  const colors = ["#f97316","#ea580c","#7c3aed","#0891b2","#059669","#c2410c","#b45309"];
  const bg = colors[(t.name?.charCodeAt(0)||0) % colors.length];
  const rating = t.rating_avg || avgR(t.ratings);
  const chips = [];
  if (t.teaching?.length > 0) {
    t.teaching.slice(0,3).forEach(e => chips.push(`${e.class} · ${(e.subjects.includes("All")?"All Subjects":e.subjects[0])}`));
  } else if (t.subjects?.length > 0) chips.push(...t.subjects.slice(0,3));

  return (
    <div style={{ background:"linear-gradient(145deg,#0f0f20,#0c0c1a)", border:"1px solid #2a2a4a", borderRadius:18, padding:"20px 16px 16px", width:190, flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", gap:8, userSelect:"none", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)", width:120, height:120, borderRadius:"50%", background:`radial-gradient(${bg}22,transparent)`, pointerEvents:"none" }} />
      <div style={{ position:"relative", marginBottom:2 }}>
        {t.photo_url
          ? <img src={t.photo_url} alt={t.name} style={{ width:80, height:80, borderRadius:"50%", objectFit:"cover", border:`3px solid ${bg}55`, display:"block" }} />
          : <div style={{ width:80, height:80, borderRadius:"50%", background:bg, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:28, border:`3px solid ${bg}55` }}>{initials}</div>
        }
        <span style={{ position:"absolute", bottom:3, right:3, width:12, height:12, borderRadius:"50%", background:t.available?"#22c55e":"#64748b", border:"2px solid #0c0c1a", display:"block" }} />
      </div>
      <div style={{ fontSize:14, fontWeight:700, color:"#fff", textAlign:"center", lineHeight:1.3, maxWidth:160 }}>{t.name}</div>
      {t.area && <div style={{ fontSize:11, color:"#94a3b8", textAlign:"center" }}>📍 {t.area}</div>}
      {chips.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:4, justifyContent:"center", marginTop:2 }}>
          {chips.map((c,i) => <span key={i} style={{ background:"rgba(249,115,22,.1)", color:"#fb923c", fontSize:10, padding:"2px 8px", borderRadius:20, border:"1px solid rgba(249,115,22,.2)", fontWeight:600, whiteSpace:"nowrap" }}>{c}</span>)}
        </div>
      )}
      <div style={{ width:"80%", height:1, background:"#252545", margin:"4px 0 2px" }} />
      <div style={{ display:"flex", gap:12, alignItems:"center", justifyContent:"center" }}>
        {t.fees_per_hour > 0 && <span style={{ fontSize:14, fontWeight:800, color:"#f97316" }}>₹{t.fees_per_hour}<span style={{ fontSize:10, fontWeight:400, color:"#64748b" }}>/mo</span></span>}
        {rating > 0 && <span style={{ fontSize:11, color:"#fbbf24", display:"flex", alignItems:"center", gap:3 }}>★ <span style={{ color:"#cbd5e1", fontWeight:600 }}>{rating}</span></span>}
      </div>
    </div>
  );
}

function TeacherMarquee({ teachers }) {
  if (!teachers.length) return null;
  const half = Math.ceil(teachers.length / 2);
  const r1 = [...teachers.slice(0,half), ...teachers.slice(0,half), ...teachers.slice(0,half)];
  const r2 = [...teachers.slice(half), ...teachers.slice(half), ...teachers.slice(half)];
  return (
    <div style={{ width:"100%", overflow:"hidden", padding:"8px 0" }}>
      <style>{`
        @keyframes scrollL { from{transform:translateX(0)} to{transform:translateX(-33.33%)} }
        @keyframes scrollR { from{transform:translateX(-33.33%)} to{transform:translateX(0)} }
        .mq-row { display:flex; gap:12px; width:max-content; }
        .mq-l { animation:scrollL 55s linear infinite; }
        .mq-r { animation:scrollR 48s linear infinite; }
      `}</style>
      <div style={{ maskImage:"linear-gradient(90deg,transparent,black 8%,black 92%,transparent)", WebkitMaskImage:"linear-gradient(90deg,transparent,black 8%,black 92%,transparent)", marginBottom:12, overflow:"hidden" }}>
        <div className="mq-row mq-l">{r1.map((t,i)=><TeacherCard key={i} t={t}/>)}</div>
      </div>
      {teachers.slice(half).length > 0 && (
        <div style={{ maskImage:"linear-gradient(90deg,transparent,black 8%,black 92%,transparent)", WebkitMaskImage:"linear-gradient(90deg,transparent,black 8%,black 92%,transparent)", overflow:"hidden" }}>
          <div className="mq-row mq-r">{r2.map((t,i)=><TeacherCard key={i} t={t}/>)}</div>
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const [sub, setSub] = useState(0);
  const [stats, setStats] = useState({ listed:null, verified:null });
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setSub(p=>(p+1)%SUBJECTS.length), 1800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/teachers/stats").then(r=>setStats(r.data)).catch(()=>{});
    api.get("/teachers", { params:{ page:1, limit:50, verified:"true" } })
      .then(r=>setTeachers(Array.isArray(r.data.teachers)?r.data.teachers:[]))
      .catch(()=>{});
  }, []);

  const listedNum   = stats.listed   !== null ? `${stats.listed   + 100}+` : "500+";
  const verifiedNum = stats.verified !== null ? `${stats.verified + 100}+` : "200+";

  return (
    <>
      <Helmet>
        <title>PadhaiPass – Find Verified Home Tutors in Kanpur</title>
        <meta name="description" content="Find trusted home tutors in Kanpur for all subjects and classes 1–12. Browse verified teachers, check ratings, and connect on WhatsApp. Free for students." />
        <meta name="keywords" content="home tutor kanpur, tuition near me, private teacher, padhaipass, home tuition kanpur, find tutor kanpur" />
        <link rel="canonical" href="https://www.padhaipass.com/" />
        <meta property="og:title" content="PadhaiPass – Find Verified Home Tutors in Kanpur" />
        <meta property="og:description" content="Find trusted home tutors in Kanpur for all subjects. Browse verified teachers and connect on WhatsApp." />
        <meta property="og:image" content="https://www.padhaipass.com/logo.png" />
        <meta property="og:url" content="https://www.padhaipass.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PadhaiPass – Find Verified Home Tutors in Kanpur" />
        <meta name="twitter:description" content="Find trusted home tutors in Kanpur. Connect on WhatsApp. Free for students." />
        <meta name="twitter:image" content="https://www.padhaipass.com/logo.png" />
        <script type="application/ld+json">{JSON.stringify({ "@context":"https://schema.org","@type":"Organization","name":"PadhaiPass","url":"https://padhaipass.com","logo":"https://padhaipass.com/logo.png","contactPoint":{"@type":"ContactPoint","telephone":"+91-9569923598","contactType":"customer support","email":"leadpadhaipass@gmail.com"} })}</script>
        <script type="application/ld+json">{JSON.stringify({ "@context":"https://schema.org","@type":"LocalBusiness","name":"PadhaiPass","description":"Find verified home tutors in Kanpur.","url":"https://padhaipass.com","telephone":"+91-9569923598","email":"leadpadhaipass@gmail.com","address":{"@type":"PostalAddress","streetAddress":"Manohar Nagar, Barra-2","addressLocality":"Kanpur","addressRegion":"Uttar Pradesh","postalCode":"208027","addressCountry":"IN"},"geo":{"@type":"GeoCoordinates","latitude":"26.4121","longitude":"80.3505"},"openingHours":"Mo-Sa 10:00-18:00","priceRange":"Free","areaServed":{"@type":"City","name":"Kanpur"} })}</script>
        <script type="application/ld+json">{JSON.stringify({ "@context":"https://schema.org","@type":"WebSite","name":"PadhaiPass","url":"https://www.padhaipass.com","potentialAction":{"@type":"SearchAction","target":"https://www.padhaipass.com/search?q={search_term_string}","query-input":"required name=search_term_string"} })}</script>
        <script type="application/ld+json">{JSON.stringify({ "@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"How do I find a home tutor in Kanpur?","acceptedAnswer":{"@type":"Answer","text":"Browse home tutors on PadhaiPass. Filter by subject, class, and locality. View profiles, check ratings, and contact on WhatsApp."}},{"@type":"Question","name":"How much does a home tutor cost in Kanpur?","acceptedAnswer":{"@type":"Answer","text":"Home tutor fees in Kanpur range from ₹400–₹700/month for classes 1–5, ₹600–₹1,200/month for 6–8, and ₹800–₹2,000/month for 9–12."}},{"@type":"Question","name":"Is PadhaiPass free for students?","acceptedAnswer":{"@type":"Answer","text":"Yes, browsing tutors is free. You can view profiles, subjects, fees, and areas at no cost."}},{"@type":"Question","name":"Are the tutors on PadhaiPass verified?","acceptedAnswer":{"@type":"Answer","text":"Select teachers are awarded a Verified Badge after manual review by our team."}},{"@type":"Question","name":"Can I find home tutors for class 10 and class 12 in Kanpur?","acceptedAnswer":{"@type":"Answer","text":"Yes. PadhaiPass has verified tutors for class 10 and class 12 CBSE/UP board in Kanpur."}}] })}</script>
      </Helmet>

      <div style={{ background:"#08080f", color:"#e2e8f0", fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif", overflowX:"hidden" }}>
        <style>{`
          @keyframes orbPulse   { 0%,100%{transform:scale(1);opacity:.6} 50%{transform:scale(1.08);opacity:1} }
          @keyframes slideUp    { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
          @keyframes badgePulse { 0%,100%{box-shadow:0 0 0 0 #f9731633} 50%{box-shadow:0 0 0 8px #f9731600} }
          @keyframes marquee3   { from{transform:translateX(0)} to{transform:translateX(-33.33%)} }
          @keyframes proofFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }

          /* hover states — work on all sizes */
          .cta-main:hover   { transform:translateY(-3px) scale(1.02)!important; box-shadow:0 16px 48px #f9731655!important; }
          .cta-sec:hover    { background:#ffffff12!important; transform:translateY(-3px)!important; }
          .feat-card:hover  { border-color:rgba(249,115,22,.35)!important; transform:translateY(-5px)!important; }
          .step-card:hover  { border-color:rgba(249,115,22,.4)!important; transform:translateY(-5px)!important; }
          .footer-link:hover{ color:#f97316!important; }

          /* ══════════════════════════════════════════
             DESKTOP ONLY  ≥ 769px
             All new desktop layout goes here.
             Nothing here affects mobile.
          ══════════════════════════════════════════ */
          @media (min-width: 769px) {
            /* hero */
            .hero-outer  { align-items: flex-start !important; }
            .hero-pad    { padding: 0 40px 40px !important; }
            .hero-split  {
              flex-direction: row !important;
              align-items: flex-start !important;
              gap: 60px !important;
              padding: 56px 0 48px !important;
            }
            .hero-left   { flex: 1 !important; text-align: left !important; min-width: 0 !important; }
            .hero-left .hero-badge { justify-content: flex-start !important; }
            .hero-left .hero-ctas  { justify-content: flex-start !important; }
            .hero-left .hero-sub   { margin-left: 0 !important; }
            .hero-right  {
              display: flex !important;
              flex-direction: column !important;
              gap: 14px !important;
              width: 356px !important;
              flex-shrink: 0 !important;
            }
            .proof-card  { animation: proofFloat 5s ease-in-out infinite; }
            /* marquee heading */
            .mq-heading  { font-size: clamp(22px,2.5vw,30px) !important; margin: 40px 0 18px !important; }
            /* steps */
            .steps-wrap  { display: flex !important; flex-direction: row !important; align-items: stretch !important; gap: 0 !important; }
            .step-card   { flex: 1 !important; }
            .step-arrow  { display: flex !important; }
            /* features */
            .feat-grid   { grid-template-columns: repeat(3,1fr) !important; }
            /* stats */
            .stats-bar   { flex-wrap: nowrap !important; }
            .stat-item   { padding: 32px 36px !important; border-right: 1px solid #1e1e3a !important; }
            .stat-item:last-child { border-right: none !important; }
            .stat-num    { font-size: 34px !important; }
            /* sections */
            .sec-pad     { padding: 88px 40px !important; }
          }

          /* ══════════════════════════════════════════
             MOBILE  ≤ 768px
             These are the exact same values as the
             original file. Desktop changes don't
             touch these.
          ══════════════════════════════════════════ */
          @media (max-width: 768px) {
            .hero-right  { display: none !important; }
            .hero-left   { text-align: center !important; }
            .hero-ctas   { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; }
            .hero-ctas > button { width: 100% !important; justify-content: center !important; }
            .steps-wrap  { grid-template-columns: 1fr !important; }
            .step-arrow  { display: none !important; }
            .feat-grid   { grid-template-columns: 1fr !important; }
            .price-grid  { grid-template-columns: 1fr !important; }
            .stats-bar   { display: grid !important; grid-template-columns: repeat(3,1fr) !important; gap: 12px !important; }
            .stat-item   { padding: 16px 8px !important; border-right: none !important; text-align: center !important; }
            .footer-inner{ grid-template-columns: 1fr 1fr !important; gap: 28px 20px !important; }
            .footer-links{ flex-direction: column !important; gap: 24px !important; }
            .sec-pad     { padding: 48px 20px !important; }
            .hero-pad    { padding: 32px 20px 28px !important; }
            .price-card  { padding: 24px 20px !important; }
            .mq-heading  { font-size: 20px !important; margin: 28px 0 14px !important; }
          }
          @media (max-width: 480px) {
            .footer-inner{ grid-template-columns: 1fr !important; gap: 32px !important; }
            .hero-outer  { align-items: flex-start !important; }
            .hero-headline{ font-size: 36px !important; letter-spacing: -0.5px !important; }
            .hero-sub    { font-size: 13px !important; }
            .hero-badge  { font-size: 11px !important; padding: 6px 12px !important; }
            .hero-ctas > button { padding: 13px 18px !important; font-size: 15px !important; }
            .stats-bar   { grid-template-columns: repeat(2,1fr) !important; }
            .stats-bar > .stat-item:last-child:nth-child(odd) { grid-column: 1/-1; }
            .sec-pad     { padding: 36px 16px !important; }
            .hero-pad    { padding: 28px 16px 24px !important; }
            .price-card  { padding: 20px 16px !important; }
          }
          @media (max-width: 360px) {
            .hero-headline{ font-size: 30px !important; }
            .sec-pad     { padding: 28px 14px !important; }
          }
        `}</style>

        {/* ══ HERO ══ */}
        <div className="hero-outer" style={{ position:"relative", minHeight:"calc(100vh - 60px)", display:"flex", alignItems:"center", overflow:"hidden" }}>
          {/* backgrounds */}
          <div style={{ position:"absolute", inset:0, background:"#0a0a16" }} />
          <div style={{ position:"absolute", inset:0, backgroundImage:"linear-gradient(rgba(255,255,255,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.035) 1px,transparent 1px)", backgroundSize:"52px 52px" }} />
          <div style={{ position:"absolute", bottom:"-20%", left:"50%", transform:"translateX(-50%)", width:"80%", height:"70%", background:"radial-gradient(ellipse at center,#f9731625 0%,transparent 70%)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", top:"10%", right:"-5%", width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle,#f9731615 0%,transparent 70%)", animation:"orbPulse 5s ease-in-out infinite", pointerEvents:"none" }} />
          <div style={{ position:"absolute", top:"35%", left:"-8%", width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,#f9731610 0%,transparent 70%)", animation:"orbPulse 6s ease-in-out infinite 2s", pointerEvents:"none" }} />

          {/* content */}
          <div className="hero-pad" style={{ position:"relative", zIndex:10, width:"100%", maxWidth:1160, margin:"0 auto", padding:"32px 20px 28px", boxSizing:"border-box" }}>

            {/* split row — column on mobile, row on desktop via CSS */}
            <div className="hero-split" style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>

              {/* LEFT: headline content */}
              <div className="hero-left" style={{ textAlign:"center", animation:"slideUp .7s ease forwards", width:"100%" }}>

                <div className="hero-badge" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(249,115,22,.1)", border:"1px solid rgba(249,115,22,.25)", borderRadius:24, padding:"7px 18px", marginBottom:18, animation:"badgePulse 2.5s ease-in-out infinite" }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:"#f97316", display:"inline-block", flexShrink:0 }} />
                  <span style={{ fontSize:13, color:"#fbbf24", fontWeight:600 }}>🇮🇳 Kanpur Ka Trusted Home Tuition Platform</span>
                </div>

                <h1 className="hero-headline" style={{ fontSize:"clamp(36px,6vw,72px)", fontWeight:900, margin:"0 0 18px", lineHeight:1.02, letterSpacing:"-2px", color:"#fff" }}>
                  Ghar Baithe Milega
                  <br />
                  <span style={{ color:"#f97316", textShadow:"0 0 48px #f9731666,0 0 96px #f9731633" }}>Best Teacher</span>
                  <br />
                  Apke Area Mein
                </h1>

                <p className="hero-sub" style={{ fontSize:16, color:"#cbd5e1", lineHeight:1.8, maxWidth:480, margin:"0 auto 28px" }}>
                  PadhaiPass pe Kanpur ke verified teachers milte hain —{" "}
                  <span style={{ color:"#fbbf24", fontWeight:600, background:"rgba(249,115,22,.1)", padding:"1px 8px", borderRadius:6, border:"1px solid rgba(249,115,22,.22)" }}>{SUBJECTS[sub]}</span>
                  {" "}ke liye bhi. Teacher se seedha WhatsApp pe baat karo. Koi hidden charge nahi.
                </p>

                <div className="hero-ctas" style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
                  <button className="cta-main" onClick={()=>window.location.href="/search"}
                    style={{ padding:"15px 36px", background:"linear-gradient(135deg,#f97316,#ea580c)", color:"#fff", border:"none", borderRadius:50, fontSize:16, fontWeight:700, cursor:"pointer", transition:"all .2s", boxShadow:"0 6px 28px rgba(249,115,22,.45)", letterSpacing:"0.02em" }}>
                    Find a Teacher
                  </button>
                  <button className="cta-sec" onClick={()=>window.location.href="/register"}
                    style={{ padding:"15px 32px", background:"transparent", color:"#e2e8f0", border:"1px solid rgba(255,255,255,0.15)", borderRadius:50, fontSize:16, fontWeight:600, cursor:"pointer", transition:"all .2s", display:"flex", alignItems:"center", gap:8 }}>
                    Register Karo <span style={{ fontSize:12 }}>▶</span>
                  </button>
                </div>
              </div>

              {/* RIGHT: proof card — shown on desktop only via CSS */}
              <div className="hero-right" style={{ display:"none" }}>
                <div className="proof-card" style={{ background:"rgba(12,12,24,0.88)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", border:"1px solid rgba(249,115,22,0.18)", borderRadius:24, padding:"26px 24px", boxShadow:"0 8px 48px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.05)", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:-50, right:-50, width:180, height:180, borderRadius:"50%", background:"radial-gradient(rgba(249,115,22,0.12),transparent)", pointerEvents:"none" }} />

                  {/* big number */}
                  <div style={{ marginBottom:20, paddingBottom:18, borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ fontSize:10, color:"#64748b", fontWeight:700, letterSpacing:3, textTransform:"uppercase", marginBottom:6 }}>Abhi Available</div>
                    <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                      <span style={{ fontSize:42, fontWeight:900, color:"#f97316", lineHeight:1 }}>{listedNum}</span>
                      <span style={{ fontSize:15, color:"#94a3b8" }}>teachers</span>
                    </div>
                    <div style={{ fontSize:12, color:"#64748b", marginTop:4 }}>Kanpur mein registered hain</div>
                  </div>

                  {/* feature list */}
                  <div style={{ display:"flex", flexDirection:"column", gap:13, marginBottom:20 }}>
                    {[
                      ["🎯","Free Demo Class","Teacher se pehle ek free class lo"],
                      ["✅","Verified Profiles","Manual review ke baad hi listing"],
                      ["💬","Direct WhatsApp","Seedha teacher se baat karo"],
                      ["📚","Nursery → B.Tech","Sabhi classes ke teachers"],
                      ["💰","₹400/mo se shuru","Budget ke andar milega teacher"],
                    ].map(([icon,title,desc]) => (
                      <div key={title} style={{ display:"flex", gap:11, alignItems:"flex-start" }}>
                        <div style={{ width:32, height:32, borderRadius:9, background:"rgba(249,115,22,0.10)", border:"1px solid rgba(249,115,22,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>{icon}</div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#fff", lineHeight:1.3 }}>{title}</div>
                          <div style={{ fontSize:11, color:"#64748b", marginTop:1 }}>{desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* subjects */}
                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:14 }}>
                    <div style={{ fontSize:10, color:"#475569", fontWeight:600, letterSpacing:2, textTransform:"uppercase", marginBottom:9 }}>Popular Subjects</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                      {["Maths","Physics","Chemistry","English","Biology","Hindi"].map(s=>(
                        <span key={s} style={{ background:"rgba(249,115,22,0.08)", border:"1px solid rgba(249,115,22,0.2)", color:"#fb923c", padding:"4px 11px", borderRadius:20, fontSize:11, fontWeight:600 }}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* mini badge */}
                <div style={{ background:"rgba(34,197,94,0.07)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:16, padding:"13px 18px", display:"flex", alignItems:"center", gap:13 }}>
                  <div style={{ width:38, height:38, borderRadius:11, background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>🏅</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#22c55e" }}>{verifiedNum} Verified Teachers</div>
                    <div style={{ fontSize:11, color:"#64748b", lineHeight:1.6 }}>Hamare team ne manually review kiya</div>
                  </div>
                </div>
              </div>
            </div>

            {/* marquee — full width, below split */}
            <div style={{ animation:"slideUp .9s ease .2s both" }}>
              {teachers.length > 0 && (
                <div className="mq-heading" style={{ textAlign:"center", fontSize:20, fontWeight:800, letterSpacing:"-0.5px", margin:"28px 0 14px" }}>
                  <span style={{ color:"#fff" }}>Verified Teachers </span>
                  <span style={{ color:"#f97316" }}>on PadhaiPass</span>
                </div>
              )}
              {teachers.length > 0
                ? <TeacherMarquee teachers={teachers} />
                : <div style={{ display:"flex", gap:12, justifyContent:"center", opacity:.25, marginTop:28 }}>
                    {[1,2,3,4].map(i=><div key={i} style={{ width:200, height:180, borderRadius:16, background:"#0e0e1c", border:"1px solid #252545" }} />)}
                  </div>
              }
            </div>
          </div>

          {/* scroll hint */}
          <div style={{ position:"absolute", bottom:24, left:"50%", transform:"translateX(-50%)", textAlign:"center", opacity:.25 }}>
            <div style={{ fontSize:9, color:"#64748b", marginBottom:5, letterSpacing:2, textTransform:"uppercase" }}>Scroll</div>
            <div style={{ width:1, height:36, background:"linear-gradient(#f97316,transparent)", margin:"0 auto" }} />
          </div>
        </div>

        {/* ══ STATS BAR ══ */}
        <div style={{ background:"linear-gradient(180deg,#0a0a16,#0e0e1c)", borderTop:"1px solid #1e1e3a", borderBottom:"1px solid #1e1e3a" }}>
          <div className="stats-bar" style={{ maxWidth:1160, margin:"0 auto", display:"flex", justifyContent:"space-around", alignItems:"stretch", flexWrap:"wrap" }}>
            {[
              [listedNum,        "Teachers Listed",  "#f97316"],
              [verifiedNum,      "Verified Teachers", "#f97316"],
              ["Nursery–B.Tech", "Sabhi Classes",    "#f97316"],
              ["Free",           "Teacher Listing",  "#22c55e"],
              ["Free",           "Student Contact",  "#22c55e"],
            ].map(([v,l,c])=>(
              <div key={l} className="stat-item" style={{ textAlign:"center", padding:"24px 20px", flex:1 }}>
                <div className="stat-num" style={{ fontSize:26, fontWeight:900, lineHeight:1, letterSpacing:"-1px", color:c }}>{v}</div>
                <div style={{ fontSize:12, color:"#94a3b8", marginTop:7, fontWeight:500 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div className="sec-pad" style={{ padding:"80px 20px", position:"relative" }}>
          <div style={{ position:"absolute", inset:0, backgroundImage:"linear-gradient(rgba(249,115,22,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(249,115,22,.03) 1px,transparent 1px)", backgroundSize:"48px 48px" }} />
          <div style={{ maxWidth:1060, margin:"0 auto", position:"relative" }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#f97316", letterSpacing:4, textTransform:"uppercase", marginBottom:10 }}>Bilkul Simple Hai</div>
              <h2 style={{ fontSize:"clamp(24px,4vw,46px)", fontWeight:900, margin:0, letterSpacing:"-1.5px", color:"#fff" }}>
                Teacher Kaise <span style={{ color:"#f97316" }}>Dhundein</span>?
              </h2>
              <p style={{ fontSize:15, color:"#64748b", marginTop:10, maxWidth:440, margin:"10px auto 0", lineHeight:1.7 }}>
                Sirf 2 minute mein apne bacho ke liye sahi teacher milega.
              </p>
            </div>

            {/* steps — grid 1-col on mobile, flex row on desktop */}
            <div className="steps-wrap" style={{ display:"grid", gridTemplateColumns:"1fr", gap:16 }}>
              {[
                ["🔍","01","Class Select Karo","Nursery se B.Tech tak apni class aur subject choose karo"],
                ["👤","02","Profile Dekho","Teacher ki ratings, fees aur area sab check karo ek jagah"],
                ["💬","03","WhatsApp pe Baat Karo","WhatsApp number lo aur seedha baat karo — koi hidden charge nahi"],
                ["📚","04","Tuition Shuru Karo","Schedule fix karo aur pehli class lo — bilkul aapke terms pe"],
              ].map(([icon,num,title,desc],i) => (
                <div key={i} style={{ display:"contents" }}>
                  <div className="step-card" style={{ background:"linear-gradient(145deg,#0e0e1c,#0b0b18)", borderRadius:18, padding:"26px 22px", border:"1px solid #1e1e3a", transition:"all .3s", position:"relative", overflow:"hidden" }}>
                    <div style={{ position:"absolute", top:14, right:16, fontSize:48, fontWeight:900, color:"#f97316", opacity:.055, lineHeight:1 }}>{num}</div>
                    <div style={{ width:48, height:48, borderRadius:13, background:"rgba(249,115,22,.10)", border:"1px solid rgba(249,115,22,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, marginBottom:16 }}>{icon}</div>
                    <div style={{ fontWeight:800, fontSize:15, marginBottom:7, color:"#fff", lineHeight:1.3 }}>{title}</div>
                    <div style={{ fontSize:12, color:"#64748b", lineHeight:1.75 }}>{desc}</div>
                  </div>
                  {i < 3 && (
                    <div className="step-arrow" style={{ display:"none", alignItems:"center", justifyContent:"center", padding:"0 4px", paddingTop:28, flexShrink:0, color:"rgba(249,115,22,0.3)", fontSize:18 }}>→</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ FEATURES ══ */}
        <div className="sec-pad" style={{ padding:"80px 20px", background:"linear-gradient(180deg,#0c0c18,#08080f)" }}>
          <div style={{ maxWidth:1060, margin:"0 auto" }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#f97316", letterSpacing:4, textTransform:"uppercase", marginBottom:10 }}>Kyun PadhaiPass?</div>
              <h2 style={{ fontSize:"clamp(24px,4vw,46px)", fontWeight:900, margin:0, letterSpacing:"-1.5px", color:"#fff" }}>
                Kanpur Ka Sabse Smart <span style={{ color:"#f97316" }}>Tuition Platform</span>
              </h2>
            </div>
            <div className="feat-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16 }}>
              {[
                ["📍","Area ke Hisaab se Search","Apne exact area mein teacher dhundo — Kidwai Nagar, Civil Lines, Govind Nagar aur bhi"],
                ["💬","WhatsApp pe Direct Baat","Teacher ka number profile pe hota hai — seedha message karo, fast aur simple"],
                ["⭐","Real Student Reviews","Asli students ke asli reviews — teacher choose karne se pehle check karo"],
                ["🎓","Sabhi Classes & Subjects","Nursery se B.Tech — Maths, Science, PCM, PCB, Commerce, JEE, NEET sab"],
                ["💰","Budget ke Andar Teacher","Monthly fee filter karo. ₹400/month se quality home tuition milti hai"],
                ["🏅","Verified Badge","Select teachers ko manual review ke baad Verified Badge milta hai"],
              ].map(([icon,title,desc],i)=>(
                <div key={i} className="feat-card" style={{ background:"linear-gradient(145deg,#0e0e1c,#0c0c18)", borderRadius:16, padding:24, border:"1px solid #1e1e3a", transition:"all .3s", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:-18, right:-18, width:90, height:90, borderRadius:"50%", background:"radial-gradient(rgba(249,115,22,0.06),transparent)", pointerEvents:"none" }} />
                  <div style={{ fontSize:28, marginBottom:14 }}>{icon}</div>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:7, color:"#f97316" }}>{title}</div>
                  <div style={{ fontSize:13, color:"#64748b", lineHeight:1.8 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ SUBJECTS TICKER ══ */}
        <div style={{ padding:"28px 0", overflow:"hidden", borderTop:"1px solid #1e1e3a", borderBottom:"1px solid #1e1e3a", background:"#0a0a14" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#334155", letterSpacing:4, textTransform:"uppercase", textAlign:"center", marginBottom:16 }}>Jin Subjects Mein Teachers Milte Hain</div>
          <div style={{ display:"flex", gap:10, animation:"marquee3 22s linear infinite", width:"max-content" }}>
            {[...SUBJECTS,...SUBJECTS,...SUBJECTS].map((s,i)=>(
              <span key={i} style={{ background:"rgba(249,115,22,.07)", border:"1px solid rgba(249,115,22,.18)", color:"#f97316", padding:"8px 22px", borderRadius:50, fontSize:13, fontWeight:600, whiteSpace:"nowrap" }}>{s}</span>
            ))}
          </div>
        </div>

        {/* ══ PRICING ══ */}
        <div className="sec-pad" style={{ padding:"80px 20px", background:"#08080f" }}>
          <div style={{ maxWidth:860, margin:"0 auto" }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <h2 style={{ fontSize:"clamp(24px,4vw,46px)", fontWeight:900, margin:"0 0 12px", letterSpacing:"-1.5px", color:"#fff" }}>
                Simple <span style={{ color:"#f97316" }}>Pricing</span>
              </h2>
              <p style={{ fontSize:14, color:"#64748b", maxWidth:440, margin:"0 auto", lineHeight:1.8 }}>
                Koi subscription nahi. Koi hidden charge nahi.
              </p>
            </div>

            <div className="price-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20 }}>
              <TiltCard style={{ height:"100%" }}>
                <div className="price-card" style={{ background:"linear-gradient(145deg,#0f0f1e,#0c0c18)", borderRadius:22, padding:"32px 28px", border:"1px solid rgba(249,115,22,0.18)", height:"100%", boxSizing:"border-box", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180, borderRadius:"50%", background:"radial-gradient(rgba(249,115,22,.09),transparent)", pointerEvents:"none" }} />
                  <div style={{ position:"relative", zIndex:1 }}>
                    <div style={{ fontSize:36, marginBottom:14 }}>🧑‍🏫</div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#f97316", letterSpacing:3, textTransform:"uppercase", marginBottom:8 }}>Teachers Ke Liye</div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#fff", marginBottom:5 }}>Free <span style={{ fontSize:13, color:"#64748b", fontWeight:400 }}>listing</span></div>
                    <div style={{ fontSize:13, color:"#64748b", lineHeight:1.8, marginBottom:18 }}>Apna profile free mein list karo — students khud aayenge.</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:11, marginBottom:26 }}>
                      {["Profile free mein list hoti hai — koi upfront cost nahi","Student demo maangega, aap free demo do","Demo ke baad sirf ek baar chhoti si platform fee","Koi commission nahi — kabhi bhi, kisi bhi class pe","Koi monthly subscription nahi"].map(t=>(
                        <div key={t} style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#cbd5e1" }}>
                          <span style={{ color:"#f97316", marginTop:2, flexShrink:0, fontWeight:700 }}>✓</span>
                          <span style={{ lineHeight:1.6 }}>{t}</span>
                        </div>
                      ))}
                    </div>
                    <button className="cta-main" onClick={()=>window.location.href="/register"}
                      style={{ width:"100%", padding:"13px 0", background:"linear-gradient(135deg,#f97316,#ea580c)", color:"#fff", border:"none", borderRadius:50, fontSize:14, fontWeight:700, cursor:"pointer", transition:"all .2s", boxShadow:"0 6px 20px rgba(249,115,22,.4)" }}>
                      Teacher Register Karo →
                    </button>
                  </div>
                </div>
              </TiltCard>

              <TiltCard style={{ height:"100%" }}>
                <div className="price-card" style={{ background:"linear-gradient(145deg,#0a1a0f,#08100a)", borderRadius:22, padding:"32px 28px", border:"1px solid rgba(34,197,94,0.18)", height:"100%", boxSizing:"border-box", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180, borderRadius:"50%", background:"radial-gradient(rgba(34,197,94,.09),transparent)", pointerEvents:"none" }} />
                  <div style={{ position:"relative", zIndex:1 }}>
                    <div style={{ fontSize:36, marginBottom:14 }}>🎓</div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#22c55e", letterSpacing:3, textTransform:"uppercase", marginBottom:8 }}>Students Ke Liye</div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#22c55e", marginBottom:5 }}>FREE <span style={{ fontSize:13, color:"#64748b", fontWeight:400 }}>search & contact</span></div>
                    <div style={{ fontSize:13, color:"#64748b", lineHeight:1.8, marginBottom:18 }}>Sare teachers browse karo. New signup pe 3 free credits milte hain.</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:11, marginBottom:26 }}>
                      {["Sabhi teachers browse karo — bilkul free","Fees, ratings, subjects — sab free mein dekho","Signup pe 3 free contacts, phir ₹29 mein 3 aur","Tuition fees pe koi commission nahi"].map(t=>(
                        <div key={t} style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#cbd5e1" }}>
                          <span style={{ color:"#22c55e", marginTop:2, flexShrink:0, fontWeight:700 }}>✓</span>
                          <span style={{ lineHeight:1.6 }}>{t}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={()=>window.location.href="/search"}
                      style={{ width:"100%", padding:"13px 0", background:"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", border:"none", borderRadius:50, fontSize:14, fontWeight:700, cursor:"pointer", transition:"all .2s", boxShadow:"0 6px 20px rgba(34,197,94,.35)" }}>
                      Teacher Dhundo →
                    </button>
                  </div>
                </div>
              </TiltCard>
            </div>
          </div>
        </div>

        {/* ══ FOOTER ══ */}
        <footer style={{ background:"#06060f", borderTop:"1px solid rgba(249,115,22,0.12)", padding:"64px 20px 0" }}>
          <div style={{ maxWidth:1100, margin:"0 auto" }}>

            {/* Top grid */}
            <div className="footer-inner" style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr 1fr 1fr", gap:40, marginBottom:52, alignItems:"start" }}>

              {/* Brand column */}
              <div>
                <div style={{ marginBottom:14 }}><Logo size={28} /></div>
                <div style={{ fontSize:13.5, color:"#64748b", maxWidth:240, lineHeight:1.9, marginBottom:20 }}>
                  Kanpur ka #1 platform for verified home tutors — koi hidden charge nahi, koi commission nahi.
                </div>
                {/* Contact pills */}
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <a href="mailto:leadpadhaipass@gmail.com" style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:12, color:"#94a3b8", textDecoration:"none", transition:"color .2s" }}
                    className="footer-link">
                    <span style={{ width:28, height:28, borderRadius:8, background:"rgba(249,115,22,.1)", border:"1px solid rgba(249,115,22,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>📧</span>
                    leadpadhaipass@gmail.com
                  </a>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:12, color:"#94a3b8" }}>
                    <span style={{ width:28, height:28, borderRadius:8, background:"rgba(249,115,22,.1)", border:"1px solid rgba(249,115,22,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>📍</span>
                    Kanpur, Uttar Pradesh – 208027
                  </span>
                </div>
              </div>

              {/* Platform column */}
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:"#f97316", textTransform:"uppercase", letterSpacing:"3px", marginBottom:18 }}>Platform</div>
                <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
                  {[["🔍 Teacher Dhundo","/search"],["📝 Teacher Register","/register"],["🔑 Login","/login"],["🏫 For Schools","/register"]].map(([l,h])=>(
                    <a key={l} href={h} className="footer-link" style={{ fontSize:13, color:"#94a3b8", textDecoration:"none", transition:"color .2s", display:"block" }}>{l}</a>
                  ))}
                </div>
              </div>

              {/* Subjects column */}
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:"#f97316", textTransform:"uppercase", letterSpacing:"3px", marginBottom:18 }}>Popular Subjects</div>
                <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
                  {["Maths Tutor","Physics Tutor","Chemistry Tutor","Biology Tutor","English Tutor","SST Tutor"].map(l=>(
                    <span key={l} style={{ fontSize:13, color:"#94a3b8", cursor:"default" }}>{l}</span>
                  ))}
                </div>
              </div>

              {/* Legal column */}
              <div>
                <div style={{ fontSize:10, fontWeight:800, color:"#f97316", textTransform:"uppercase", letterSpacing:"3px", marginBottom:18 }}>Company</div>
                <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
                  {[["About Us","/about"],["Privacy Policy","/privacy"],["Terms of Service","/terms"],["Refund Policy","/refund"],["Contact Us","/contact"]].map(([l,h])=>(
                    <a key={l} href={h} className="footer-link" style={{ fontSize:13, color:"#94a3b8", textDecoration:"none", transition:"color .2s", display:"block" }}>{l}</a>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height:1, background:"linear-gradient(90deg,transparent,rgba(249,115,22,.2),transparent)", marginBottom:24 }} />

            {/* Bottom bar */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14, paddingBottom:28 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:12, color:"#475569" }}>© 2025 PadhaiPass Technologies.</span>
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:700, color:"#f97316", background:"rgba(249,115,22,.08)", border:"1px solid rgba(249,115,22,.2)", borderRadius:20, padding:"3px 10px" }}>
                  🇮🇳 Made in Kanpur
                </span>
              </div>
              <div style={{ fontSize:12, color:"#334155" }}>
                All rights reserved · Kanpur, UP
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
